import { queryOptions, type DataTag } from "@tanstack/react-query";
import type { components } from "@aura/contract";
import type { JobCounts } from "@/lib/jobs/list/jobLists";

type Rollout = components["schemas"]["Rollout"];
type MoveAlertList = components["schemas"]["MoveAlertList"];

/** A key that names the type of the answer read under it: every read of it, on the server or in the browser, and every patch, is checked against that one type. */
const answering =
  <T>() =>
  <K extends readonly unknown[]>(key: K): DataTag<K, T> =>
    key as DataTag<K, T>;

/** The read of a tagged key: its answer must be the key's type, so the server's read and the browser's cannot disagree. */
export const readOf = <K extends readonly unknown[], T>(queryKey: DataTag<K, T>, queryFn: () => Promise<NoInfer<T>>) =>
  queryOptions({ queryKey, queryFn });

/**
 * Every key in the browser's read cache (lib/queryClient.ts), by business noun and id: a job's reads all start with `["job", id]`,
 * so a write to the job can mark them all at once. A new read adds its key here, never inline (docs/data-layer.md).
 */
export const queryKeys = {
  /** A write on the one write path (lib/mutations/write.ts), by its name and, for a job's, the job: what a test or devtools finds it by. */
  write: (name: string, jobId?: string) =>
    jobId === undefined ? (["write", name] as const) : (["write", name, jobId] as const),
  /** The ways a job can be added by hand: schemes, administrators and channels as the API holds them now (`GET /api/new-job`). */
  newJobChoices: ["new-job-choices"] as const,
  /** One assessor's days from `from` to `to` (dates), with the journey to each booked visit (`GET /api/assessors/[id]/schedule`). */
  assessorSchedule: (id: string, from: string, to: string) => ["assessor", id, "schedule", from, to] as const,
  /** Miles from where an assessor would set off for one half-day to each waiting job, for the `version` of the bookings it was read at. */
  assessorDistances: (id: string, date: string, slot: string, version: number) =>
    ["assessor", id, "distances", date, slot, version] as const,
  /** Every list of the feedback people sent: the prefix a change to a piece of it makes stale. */
  feedback: ["feedback"] as const,
  /** The feedback people sent that stands at `filter` (new, done, archived or all), newest first, a page at a time (`GET /api/feedback?triage=`). */
  feedbackList: (filter: string) => ["feedback", filter] as const,
  /** Which kinds of a job's move post a card to the staff channel (`GET /api/settings?store=alerts`): Settings > Alerts. */
  moveAlerts: answering<MoveAlertList>()(["move-alerts"] as const),
  /** The administrator connections (`GET /api/admin/administrators/connections`). */
  adminConnections: ["admin", "administrators", "connections"] as const,
  /** The latest integration runs, narrowed to one status ("" for all). */
  adminRuns: (status: string) => ["admin", "administrators", "runs", status] as const,
  /** The latest integration runs, whatever status they were narrowed to: what a retry makes stale. */
  adminRunLists: ["admin", "administrators", "runs"] as const,
  /** One connection's runs over the last day or week, newest first (Settings › System's tool rows and their detail). */
  connectionRuns: (connectionId: string, window: string) =>
    ["admin", "administrators", "runs", "connection", connectionId, window] as const,
  /** The app's own deliveries (`GET /api/admin/administrators/effects`), in one state ("" for every one not settled well). */
  adminEffects: (state: string) => ["admin", "deliveries", "effects", state] as const,
  /** Every list of the app's own deliveries: what trying one again or setting it aside makes stale. */
  adminEffectLists: ["admin", "deliveries", "effects"] as const,
  /** Everyone a job can be given to, by id and name (`GET /api/users/assignable`): the names shown beside "who". */
  peopleNames: ["people-names"] as const,
  /** Who sees the management-only pages, by name (`GET /api/users/assignable?group=managers`): the padlock's tooltip. */
  leadership: ["leadership"] as const,
  /** Where the move to Aura Core stands (`GET /api/rollout`): whether this deploy can tell the team about feedback. */
  rollout: answering<Rollout>()(["rollout"] as const),
  /** Whether an evidence file is sound before it is drawn (`GET /api/evidence/[id]?check=1`). */
  evidenceCheck: (id: string) => ["evidence-check", id] as const,
  /** The diary's post-works EPC visits across every job (`GET /api/visits?kind=post-works-epc`): the Calendar's Closeout days. */
  postEpcVisits: ["post-epc-visits"] as const,
  /** Everyone who can be booked (`GET /api/assessors`, without a window): the Calendar's picker. */
  assessorList: ["assessors"] as const,
  /** The survey team with their days from the Monday `from`, eight weeks a page (`GET /api/assessors?from=&to=`): Settings > Assessors. */
  assessorTeam: (from: string) => ["assessor-team", from] as const,
  /** The survey team from whatever Monday: what a change to the team or to a day makes stale. */
  assessorTeams: ["assessor-team"] as const,
  /** A person's picture, read until it can be drawn (`GET /api/avatar/[id]`): the Users list waits for them. */
  avatarPicture: (id: string) => ["avatar", id] as const,
  /** What the shell greets the person with, read once per page load and sign-in (`GET /api/profile`, lib/os/greetings.ts). */
  greetings: (signIn: string | null) => ["greetings", signIn] as const,
  /** What a move of a job would meet, for the target and the `stamp` of what the job held when it was asked. */
  jobMoveCheck: (id: string, target: string, stamp: string) => ["job", id, "move-check", target, stamp] as const,
  /** What the customer receives for one version of a job's quote (`GET /api/quote-design/customer-preview`). */
  jobCustomerPreview: (id: string, versionId: string) => ["job", id, "customer-preview", versionId] as const,
  /** The reasons a job can stop for (`GET /api/not-going-ahead-reasons`): the catalogue's, the same for every job. */
  notGoingAheadReasons: ["not-going-ahead-reasons"] as const,
  /** The catalogue reasons a hold can carry (`GET /api/hold-reasons`). */
  holdReasons: ["hold-reasons"] as const,
  /** What Log contact offers: the reasons a contact did not get through and the topics of one that did (`GET /api/contact-choices`). */
  contactChoices: ["contact-choices"] as const,
  /** Everybody the administrator can manage (`GET /api/admin/users`). */
  adminUsers: ["admin", "users"] as const,
  /** The rows of one list, by its id (`GET /api/job-lists?list=`, lib/jobs/list/jobLists.ts). */
  jobList: (list: string) => ["job-list", list] as const,
  /** Every list's rows: what a write that moves a job marks stale. */
  jobLists: ["job-list"] as const,
  /**
   * All jobs with something on the diary in one calendar month (`YYYY-MM`): a visit or an installation start (`GET /api/job-lists`
   * with `scheduledFrom`/`scheduledTo`). Under All jobs' own key, so a write that moves a job marks it stale with every list.
   */
  scheduledJobs: (month: string) => ["job-list", "all:everything", { scheduledIn: month }] as const,
  /** Every install job's start date and booking count (`GET /api/installations`): a column of the lists, marked with them. */
  installSummaries: ["installations"] as const,
  /** Every Closeout job's record (`GET /api/closeouts`): the lists' status and invoice columns, marked with them. */
  closeoutSummaries: ["closeouts"] as const,
  /** How many jobs each list the navigation counts holds (`GET /api/job-counts`). */
  jobCounts: answering<JobCounts>()(["job-counts"] as const),
  /** The jobs the person has not opened since they arrived in the list they are in: the lists' "New" edge (`GET /api/job-views`). */
  caseViews: answering<string[]>()(["job-views"] as const),
  /** Which job an address (`/job/<ref>`, an old link) names: its id. */
  jobAt: (address: string) => ["job-at", address] as const,
  /** What a job page holds about its job (`GET /api/job-records/[ref]`): its lead and its quote row. */
  jobRecord: (id: string) => ["job", id, "record"] as const,
  /** Everything read about one job. */
  job: (id: string) => ["job", id] as const,
  /** A job's filed photos, videos and documents (`GET /api/jobs/[id]/media`). */
  jobMedia: (id: string) => ["job", id, "media"] as const,
  /** What Check eligibility stored on a job: its checks, its EPC rows and the title check (`GET /api/jobs/[id]/validation`). */
  jobValidation: (id: string) => ["job", id, "validation"] as const,
  /** What the intake engine says a job needs (`GET /api/jobs/[id]/intake-rules`). */
  jobIntakeRules: (id: string) => ["job", id, "intake-rules"] as const,
  /** A job's household and supporting documents, from the Files module (`GET /api/jobs/[id]/documents`). */
  jobDocuments: (id: string) => ["job", id, "documents"] as const,
  /** A job's PAS 2035 assessment pack (`GET /api/survey`). */
  jobSurveyDocuments: (id: string) => ["job", id, "survey-documents"] as const,
  /** A job's PaaS Hub survey photos (`GET /api/survey/photos`). */
  jobSurveyPhotos: (id: string) => ["job", id, "survey-photos"] as const,
  /** A job's comments (`GET /api/notes`). */
  jobNotes: (id: string) => ["job", id, "notes"] as const,
  /** What the API holds for a job's returned assessment (`GET /api/assessment`). */
  jobAssessment: (id: string) => ["job", id, "assessment"] as const,
  /** Where Send quote's invite goes, which comes from the frozen customer on the version it was read for. */
  jobSendTarget: (id: string, versionKey: string) => ["job", id, "send-target", versionKey] as const,
  /** Every read of a job's upload link, whatever booking it was for. */
  jobUploadLinks: (id: string) => ["job", id, "upload-link"] as const,
  /** The assessor upload link of a job's booking (`GET /api/jobs/[id]/upload-link`). */
  jobUploadLink: (id: string, bookingKey: string) => ["job", id, "upload-link", bookingKey] as const,
  /** The signed-in person's profile (`GET /api/profile`). */
  ownProfile: ["profile"] as const,
  /** A job's installation: dates, note, bookings and extra work (`GET /api/jobs/[id]/installation`). */
  jobInstall: (id: string) => ["job", id, "install"] as const,
  /** Every try at reaching a job's household (`GET /api/jobs/[id]/contact-attempts`). */
  jobContactAttempts: (id: string) => ["job", id, "contact-attempts"] as const,
  /** What has happened to a job, and who did it (`GET /api/job-history`). */
  jobHistory: (id: string) => ["job", id, "history"] as const,
  /** A job's target submission month, with the Scope of Work ETag it was read under. */
  jobTargetMonth: (id: string) => ["job", id, "target-month"] as const,
  /** Where Capture hands a job to, and where Book assessment sends it back to (`GET /api/jobs/[id]/routing`). */
  jobRouting: (id: string) => ["job", id, "routing"] as const,
  /** Every read of a job's visits, whatever booking it was for. */
  jobVisitReads: (id: string) => ["job", id, "visits"] as const,
  /** A job's visits as the booking sync holds them, for the booking they were read for. */
  jobVisits: (id: string, bookingKey: string) => ["job", id, "visits", bookingKey] as const,
  /** Where one postcode is (`GET /api/areas`), by the postcode without spaces in capitals. */
  postcodePlace: (postcode: string) => ["postcode", postcode] as const,
  /** Every week's free half-days. */
  availabilityWeeks: ["availability"] as const,
  /** Every job's reads, to be narrowed by `predicate` (a read that is not named by job id). */
  jobs: ["job"] as const,
  /** One week's free half-days, from `from` to `to` (dates): not a job's, so a booking drops them all. */
  availability: (from: string, to: string) => ["availability", from, to] as const,
  /** What a job's design PDFs were read as, for every version. */
  jobReadings: (id: string) => ["job", id, "readings"] as const,
  /** What one version's design PDFs were read as (`GET /api/quote-design/readings`). */
  jobVersionReadings: (id: string, versionId: string) => ["job", id, "readings", versionId] as const,
  /** What stands between a job's Scope of Work and Sign off, as read for the version in this `stamp` (lib/works/sow/scopeOfWork.ts versionStamp). */
  jobScopeIssues: (id: string, stamp: string) => ["job", id, "scope-issues", stamp] as const,
  /** The API's field catalogue for the Scope of Work: the same for every job. */
  designFields: ["design-fields"] as const,
  /** A job's Closeout record, with the ETag it was read under (`GET /api/jobs/[id]/closeout`). */
  jobCloseout: (id: string) => ["job", id, "closeout"] as const,
  /** A job's retrofit coordinator dates, at any step, with the ETag they were read under (`GET /api/jobs/[id]/retrofit-coordinator`). */
  jobRetrofitCoordinator: (id: string) => ["job", id, "retrofit-coordinator"] as const,
  /** A job's Submission facts (portal status, the pack's reference numbers), with the ETag they were read under (`GET /api/jobs/[id]/submission`). */
  jobSubmission: (id: string) => ["job", id, "submission"] as const,
  /** A job's eligibility evidence at the administrator, with the ETag it was read under (`GET /api/jobs/[id]/eligibility-evidence`). */
  jobEligibilityEvidence: (id: string) => ["job", id, "eligibility-evidence"] as const,
  /** A job's scheme application (`GET /api/jobs/[id]/application`). */
  jobApplication: (id: string) => ["job", id, "application"] as const,
  /** The administrator's form for a job, built from the household's details as they were when it was read. */
  jobApplicationForm: (id: string, detailsKey: string) => ["job", id, "application-form", detailsKey] as const,
  /**
   * The Scope of Work's ETag as the screen last read it (the versions read and every write's answer), sent as If-Match. Not under
   * the job's prefix: a write to the job does not make it stale, only a newer answer replaces it.
   */
  scopeEtag: (id: string) => answering<string | null>()(["scope-etag", id] as const),
  /** Every job's Scope of Work ETag: kept for the whole visit, never collected while it waits for the next write. */
  scopeEtags: ["scope-etag"] as const,
  /** A job's Scope of Work versions, newest first (`GET /api/jobs/[id]/scope-of-work/revisions`). */
  jobVersions: (id: string) => ["job", id, "versions"] as const,
};
